import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Popover,
  Divider,
  Typography,
  makeStyles,
  Button
} from '@material-ui/core';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { useMeContext } from '~/modules/me';
import { getTodayForUser } from '~/modules/common/dates/today';
import DateField from '../../DateField';

const onDateChange = setDate => date => setDate(date);

const useStyles = makeStyles(theme => ({
  text: {
    ...theme.typography.caption,
    fontWeight: theme.typography.fontWeightBold
  },
  estimatedHours: {
    paddingRight: theme.spacing(1)
  },
  availableHours: {
    paddingRight: theme.spacing(1)
  },
  allocatedToTask: {
    paddingRight: theme.spacing(2.25)
  },
  footer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'end'
  },
  requestButton: {
    marginLeft: theme.spacing(1)
  },
  datePicker: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  title: {
    paddingBottom: theme.spacing(1)
  }
}));

const usePopoverStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2)
  }
}));
const useDateStyles = makeStyles(theme => ({
  input: {
    marginLeft: theme.spacing(1)
  }
}));

const hours = '128.00h';

const AddAllocationEditor = ({ children, anchorEl, setAnchorEl }) => {
  const me = useMeContext();
  const { formatMessage } = useIntl();

  const { day, month, year } = getTodayForUser(me);
  const today = DateTime.utc(year, month, day);
  const [startDate, setStartDate] = useState(today.toISO());
  const [endDate, setEndDate] = useState(today.plus({ day: 1 }).toISO());

  const classes = useStyles();
  const dateClasses = useDateStyles();
  const popoverClasses = usePopoverStyles();

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  return (
    <>
      {children}

      {Boolean(anchorEl) && (
        <Popover
          id="add-allocation-editor"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={onClose}
          classes={popoverClasses}
        >
          <Typography variant="body2" className={classes.title}>
            {formatMessage({ id: 'addAllocationPopover.setRequest' })}
          </Typography>
          <div className={classes.datePicker}>
            <DateField
              clearable={false}
              variant="outlined"
              value={startDate}
              editable
              ariaLabel={formatMessage({
                id: 'addAllocationPopover.requestStartDate'
              })}
              onChange={onDateChange(setStartDate)}
              label={formatMessage({
                id: 'addAllocationPopover.requestStartDate'
              })}
            />

            <DateField
              clearable={false}
              variant="outlined"
              value={endDate}
              editable
              classes={dateClasses}
              onChange={onDateChange(setEndDate)}
              ariaLabel={formatMessage({
                id: 'addAllocationPopover.requestEndDate'
              })}
              label={formatMessage({
                id: 'addAllocationPopover.requestEndDate'
              })}
            />
          </div>

          <div>
            <Typography
              className={classNames(classes.text, classes.estimatedHours)}
              variant="caption"
            >
              {formatMessage({ id: 'addAllocationPopover.estimatedHours' })}:
            </Typography>
            <div>
              <Typography
                className={classNames(classes.text, classes.availableHours)}
                variant="caption"
              >
                {formatMessage({
                  id: 'addAllocationPopover.availableProjectHours'
                })}
                :
              </Typography>
              <Typography className={classes.text} variant="caption">
                {hours}
              </Typography>
            </div>
            <Divider />
            <div>
              <Typography
                className={classNames(classes.text, classes.allocatedToTask)}
                variant="caption"
              >
                {formatMessage({
                  id: 'addAllocationPopover.allocatedToTask'
                })}
              </Typography>
              <Typography className={classes.text} variant="caption">
                {hours}
              </Typography>
            </div>
          </div>
          <div className={classes.footer}>
            <Button variant="contained" onClick={onClose}>
              {formatMessage({ id: 'button.cancel' })}
            </Button>

            <Button
              color="primary"
              className={classes.requestButton}
              variant="contained"
              onClick={onClose}
            >
              {formatMessage({ id: 'addAllocationPopover.requestResource' })}
            </Button>
          </div>
        </Popover>
      )}
    </>
  );
};

AddAllocationEditor.propTypes = {
  children: PropTypes.node.isRequired,
  anchorEl: PropTypes.object,
  setAnchorEl: PropTypes.func
};

export default AddAllocationEditor;
